// useReportIt (v7 §6.1, MV-1077) — the stateful bridge between a Report It
// dialog and the submission flow. Owns the idempotency nonce (stable for the
// dialog's lifetime), the submit lifecycle (idle → submitting → success/error),
// and the default video-element lookup.
//
// Decoupled from MytaverseEventProvider: the report context (environment,
// event, session id, correlation entities, optional getStats) is passed in, so
// the hook is unit-testable and the component that mounts it owns the (thin)
// mapping from live context to this config.

import { useCallback, useMemo, useState } from 'react';

import {
  runReportItSubmission,
  ReportDraft,
  ReportSubmissionPhase,
} from './submitReportFlow';
import { captureReportItSnapshot } from './captureSnapshot';
import { getStreamConnectionSnapshot } from './connectionStateStore';
import {
  submitReport,
  ReportCorrelationEntities,
  ReportAttachmentFile,
  SubmitReportResponse,
  ReportIncidentType,
  ReportSymptomHint,
  ReportSeverityHint,
} from './reportItApi';
import { generateClientReportNonce } from './reportNonce';

const GAMECAST_VIDEO_ELEMENT_ID = 'gamecastVideoTag';

export interface ReportItContext {
  environment: string;
  eventId?: string;
  browserSessionId: string;
  entities?: ReportCorrelationEntities;
  /** Optional WebRTC stats provider — omit until the GameCast peer connection
   *  is exposed; the capture degrades to media-element + black-frame signals. */
  getStats?: () => Promise<RTCStatsReport>;
  /** Override the video-element lookup (defaults to #gamecastVideoTag). */
  getVideoElement?: () => HTMLVideoElement | null;
}

export interface ReportFormValues {
  incidentType: ReportIncidentType;
  description?: string;
  symptomHints?: ReportSymptomHint[];
  severityHint?: ReportSeverityHint;
  attachDiagnostics?: boolean;
  attachments?: ReportAttachmentFile[];
}

export type ReportItStatus = 'idle' | 'submitting' | 'success' | 'error';

export type ReportItPhase = 'idle' | ReportSubmissionPhase;

export interface UseReportItResult {
  status: ReportItStatus;
  /** Finer-grained progress within `submitting` (capturing/submitting/uploading). */
  phase: ReportItPhase;
  /** Per-attachment upload progress (0..1), keyed by declaration index. */
  uploadProgress: Record<number, number>;
  /** True when the report was created but its screenshots failed to upload. */
  attachmentUploadFailed: boolean;
  error?: string;
  result?: SubmitReportResponse;
  submit: (values: ReportFormValues) => Promise<SubmitReportResponse | undefined>;
  reset: () => void;
}

export function useReportIt(context: ReportItContext): UseReportItResult {
  const {
    environment,
    eventId,
    browserSessionId,
    entities,
    getStats,
    getVideoElement: getVideoElementOverride,
  } = context;

  const [status, setStatus] = useState<ReportItStatus>('idle');
  const [phase, setPhase] = useState<ReportItPhase>('idle');
  const [uploadProgress, setUploadProgress] = useState<Record<number, number>>(
    {},
  );
  const [attachmentUploadFailed, setAttachmentUploadFailed] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<SubmitReportResponse | undefined>(undefined);

  // Stable across retries of a single report attempt (so a resubmit collapses
  // onto the same report, v7 §10.3), but regenerated by reset() when a new
  // report begins. Without that, reopening the dialog reuses the nonce and the
  // next report idempotently collapses onto the previous one.
  const [nonce, setNonce] = useState<string>(() => generateClientReportNonce());

  const getVideoElement = useMemo(
    () =>
      getVideoElementOverride ??
      ((): HTMLVideoElement | null =>
        document.getElementById(
          GAMECAST_VIDEO_ELEMENT_ID,
        ) as HTMLVideoElement | null),
    [getVideoElementOverride],
  );

  const submit = useCallback(
    async (
      values: ReportFormValues,
    ): Promise<SubmitReportResponse | undefined> => {
      setStatus('submitting');
      setError(undefined);
      setUploadProgress({});
      setAttachmentUploadFailed(false);

      const draft: ReportDraft = {
        environment,
        eventId,
        browserSessionId,
        clientReportNonce: nonce,
        incidentType: values.incidentType,
        occurredAt: new Date().toISOString(),
        description: values.description,
        symptomHints: values.symptomHints,
        severityHint: values.severityHint,
        attachDiagnostics: values.attachDiagnostics,
        attachments: values.attachments,
      };

      try {
        const res = await runReportItSubmission(
          {
            capture: captureReportItSnapshot,
            submit: submitReport,
            getVideoElement,
            getStats,
            // Read directly from the page-lifetime store: connection-state
            // history is meaningful even for reports filed after leaving the
            // experience ("it kept freezing, so I quit").
            getConnectionState: getStreamConnectionSnapshot,
            getEntities: entities ? () => entities : undefined,
            onPhaseChange: setPhase,
            onAttachmentProgress: (index, fraction) =>
              setUploadProgress((prev) => ({ ...prev, [index]: fraction })),
            onAttachmentError: () => setAttachmentUploadFailed(true),
          },
          draft,
        );

        setResult(res);
        setStatus('success');

        // NOTE (MV-1077): analysis is intentionally NOT triggered here. To save
        // cost, correlation/root-cause analysis is operator-initiated from the
        // Observability Hub (the "Analyse" button on the report entry), the same
        // way the top-error flow works. The report is persisted as `submitted`
        // and waits for an explicit analyse action.
        return res;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to submit report';
        setError(message);
        setStatus('error');
        // eslint-disable-next-line no-console
        console.error(`Failed to submit Report It report: ${message}`, {
          eventId,
        });

        return undefined;
      }
    },
    [environment, eventId, browserSessionId, entities, getStats, nonce, getVideoElement],
  );

  const reset = useCallback((): void => {
    setStatus('idle');
    setPhase('idle');
    setUploadProgress({});
    setAttachmentUploadFailed(false);
    setError(undefined);
    setResult(undefined);
    // A reset marks the start of a new report — give it a fresh idempotency
    // nonce so it doesn't collapse onto the previously submitted report.
    setNonce(generateClientReportNonce());
  }, []);

  return {
    status,
    phase,
    uploadProgress,
    attachmentUploadFailed,
    error,
    result,
    submit,
    reset,
  };
}
